import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';


import AccountSummary from './components/AccountSummary';
import SearchFilters from './components/SearchFilters';
import TripCard from './components/TripCard';
import RecentActivity from './components/RecentActivity';
import BulkActions from './components/BulkActions';
import SEOHead from '../../components/SEOHead';
import { tripService } from '../../services/tripService';
import { tripPDFService } from '../../services/tripPDFService';


import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useLocation } from 'react-router-dom';



const UserDashboard = () => {
  const { user, userProfile, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [showWelcomeMessage, setShowWelcomeMessage] = useState(false);
  const [trips, setTrips] = useState([]);
  const [filteredTrips, setFilteredTrips] = useState([]);
  const [selectedTrips, setSelectedTrips] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('date-desc');
  const [filters, setFilters] = useState({ status: '', date: '' });
  const [stats, setStats] = useState({});
  const [activities, setActivities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [downloadingTripId, setDownloadingTripId] = useState(null);

  // Check if user just completed trip confirmation flow
  const fromTripConfirmation = location?.state?.fromTripConfirmation;

  // ✅ NEW: Helper function to get the next upcoming or in-progress trip
  const getNextTrip = () => {
    if (!trips || trips?.length === 0) return null;

    const currentDate = new Date();
    currentDate?.setHours(0, 0, 0, 0);

    // Find trips that are either upcoming or in-progress
    const relevantTrips = trips?.filter(trip => {
      if (!trip?.startDate) return false;

      const startDate = new Date(trip.startDate);
      startDate?.setHours(0, 0, 0, 0);

      const endDate = new Date(trip.endDate);
      endDate?.setHours(23, 59, 59, 999);

      // Include trips that haven't ended yet (upcoming or in-progress)
      return currentDate <= endDate && trip?.status !== 'cancelled';
    });

    // Sort by start date (earliest first)
    const sortedTrips = relevantTrips?.sort((a, b) => {
      const dateA = new Date(a.startDate);
      const dateB = new Date(b.startDate);
      return dateA - dateB;
    });

    return sortedTrips?.[0] || null;
  };

  // ✅ NEW: Check if a trip is currently in progress
  const isTripInProgress = (trip) => {
    if (!trip?.startDate || !trip?.endDate) return false;

    const currentDate = new Date();
    currentDate?.setHours(0, 0, 0, 0);

    const startDate = new Date(trip.startDate);
    startDate?.setHours(0, 0, 0, 0);

    const endDate = new Date(trip.endDate);
    endDate?.setHours(23, 59, 59, 999);

    return currentDate >= startDate && currentDate <= endDate;
  };

  const nextTrip = getNextTrip();
  const isNextTripInProgress = nextTrip ? isTripInProgress(nextTrip) : false;

  useEffect(() => {
    if (!loading && !user) {
      navigate('/user-login', { replace: true });
      return;
    }

    if (user && !loading) {
      loadUserTrips();
      
      // Show welcome message if coming from trip confirmation
      if (fromTripConfirmation) {
        setShowWelcomeMessage(true);
      }
    }
  }, [user, loading, navigate, fromTripConfirmation]);

  // Apply filters and search with DATE-BASED CATEGORIZATION
  useEffect(() => {
    let filtered = [...trips];
    
    // Get today's date for comparison
    const today = new Date();
    today?.setHours(0, 0, 0, 0);
    
    // Apply search term
    if (searchTerm?.trim()) {
      filtered = filtered?.filter(trip => 
        trip?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        trip?.destination?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        trip?.park?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        trip?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase())
      );
    }
    
    // Apply status filter with DATE-BASED LOGIC
    if (filters?.status) {
      if (filters?.status === 'upcoming') {
        // Upcoming = trips with start date in the future
        filtered = filtered?.filter(trip => {
          if (!trip?.startDate) return false;
          const startDate = new Date(trip.startDate);
          startDate?.setHours(0, 0, 0, 0);
          return startDate >= today && trip?.status !== 'cancelled';
        });
      } else if (filters?.status === 'planning') {
        // Planning = trips in planning status or without confirmed dates
        filtered = filtered?.filter(trip => {
          return trip?.status === 'planning' || trip?.status === 'draft' || !trip?.startDate;
        });
      } else if (filters?.status === 'completed') {
        // Completed = trips with start date in the past OR marked as completed
        filtered = filtered?.filter(trip => {
          if (!trip?.startDate) return trip?.status === 'completed';
          const startDate = new Date(trip.startDate);
          startDate?.setHours(0, 0, 0, 0);
          return startDate < today || trip?.status === 'completed';
        });
      } else {
        // Other statuses use regular status filter
        filtered = filtered?.filter(trip => trip?.status === filters?.status);
      }
    }
    
    // Apply date filter (if needed in the future)
    if (filters?.date) {
      // Add date filtering logic here if needed
    }
    
    // UPDATED: Apply sorting with ascending/descending options
    if (sortBy === 'date-desc') {
      // Trip date - newest first (default)
      filtered?.sort((a, b) => {
        const dateA = new Date(a?.startDate || a?.createdAt || 0);
        const dateB = new Date(b?.startDate || b?.createdAt || 0);
        return dateB - dateA;
      });
    } else if (sortBy === 'date-asc') {
      // Trip date - oldest first
      filtered?.sort((a, b) => {
        const dateA = new Date(a?.startDate || a?.createdAt || 0);
        const dateB = new Date(b?.startDate || b?.createdAt || 0);
        return dateA - dateB;
      });
    } else if (sortBy === 'created-desc') {
      // Creation date - newest first
      filtered?.sort((a, b) => {
        const dateA = new Date(a?.createdAt || 0);
        const dateB = new Date(b?.createdAt || 0);
        return dateB - dateA;
      });
    } else if (sortBy === 'created-asc') {
      // Creation date - oldest first
      filtered?.sort((a, b) => {
        const dateA = new Date(a?.createdAt || 0);
        const dateB = new Date(b?.createdAt || 0);
        return dateA - dateB;
      });
    } else if (sortBy === 'name-asc') {
      // Name A-Z
      filtered?.sort((a, b) => {
        const aName = (a?.title || a?.destination || '')?.toLowerCase();
        const bName = (b?.title || b?.destination || '')?.toLowerCase();
        return aName?.localeCompare(bName);
      });
    } else if (sortBy === 'name-desc') {
      // Name Z-A
      filtered?.sort((a, b) => {
        const aName = (a?.title || a?.destination || '')?.toLowerCase();
        const bName = (b?.title || b?.destination || '')?.toLowerCase();
        return bName?.localeCompare(aName);
      });
    } else if (sortBy === 'status') {
      // Define status priority for sorting
      const statusOrder = { planning: 1, upcoming: 2, completed: 3, cancelled: 4 };
      filtered?.sort((a, b) => {
        const statusA = statusOrder?.[a?.status] || 999;
        const statusB = statusOrder?.[b?.status] || 999;
        return statusA - statusB;
      });
    }
    
    setFilteredTrips(filtered);
  }, [trips, searchTerm, filters, sortBy]);

  const loadUserTrips = async () => {
    if (!user) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Load trips, stats, and activities in parallel
      const [tripsData, statsData, activitiesData] = await Promise.all([
        tripService?.getUserTrips(user?.id),
        tripService?.getTripStats(user?.id),
        tripService?.getUserActivities(user?.id, 10)
      ]);

      setTrips(tripsData || []);
      setStats(statsData || {});
      setActivities(activitiesData || []);
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      setError('Failed to load dashboard data. Please try refreshing the page.');
    } finally {
      setIsLoading(false);
    }
  };

  const dismissWelcomeMessage = () => {
    setShowWelcomeMessage(false);
    // Clear the state to prevent showing again on refresh
    window.history?.replaceState(null, '', '/user-dashboard');
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleFilter = (newFilters) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setFilters({ status: '', date: '' });
  };

  const handleDeleteTrip = async (tripId) => {
    if (window.confirm('Are you sure you want to delete this trip?')) {
      try {
        await tripService?.deleteTrip(tripId);
        
        // ✅ NEW: Log deletion activity
        await tripService?.logActivity(
          user?.id,
          tripId,
          'deleted',
          'Deleted trip from dashboard',
          {
            deleted_at: new Date()?.toISOString(),
            location: 'dashboard'
          }
        );

        // Refresh trips and activities
        const [tripsData, activitiesData] = await Promise.all([
          tripService?.getUserTrips(user?.id),
          tripService?.getUserActivities(user?.id)
        ]);
        
        setTrips(tripsData);
        setActivities(activitiesData);
        setMessage({ type: 'success', text: 'Trip deleted successfully' });
      } catch (error) {
        console.error('Delete error:', error);
        setMessage({ type: 'error', text: 'Failed to delete trip' });
      }
    }
  };

  const handleDuplicateTrip = async (trip) => {
    try {
      // For now, just log - implement duplicate functionality later
      console.log('Duplicating trip:', trip?.id);
    } catch (err) {
      console.error('Failed to duplicate trip:', err);
    }
  };

  const handleDownloadTrip = async (tripId) => {
    if (!tripId) return;

    setDownloadingTripId(tripId);
    setMessage({ type: 'info', text: 'Processing download...' });

    try {
      // ✅ UPDATED: Fetch complete trip data with itinerary
      const tripData = await tripService?.getTripById(tripId);

      // ✅ UPDATED: Update message to show PDF generation is in progress
      setMessage({ type: 'info', text: 'Generating PDF...' });

      // ✅ UPDATED: Convert itinerary to the format expected by PDF service
      const formattedTripData = {
        ...tripData,
        participants: tripData?.travelers,
        estimatedCost: tripData?.totalCost?.toString(),
        transportation: 'Personal Vehicle', // Default value
        featuredParks: [tripData?.park || tripData?.destination],
        activityTypes: tripData?.activities || [],
        // Convert itinerary to days format
        days: convertItineraryToDaysForPDF(tripData?.itinerary, tripData?.startDate)
      };

      // ✅ UPDATED: Generate PDF directly on dashboard
      const pdfBlob = await tripPDFService?.generatePDF(formattedTripData);

      // ✅ UPDATED: Trigger download
      const url = URL?.createObjectURL(pdfBlob);
      const link = document?.createElement('a');
      link.href = url;
      link.download = `${tripData?.title || tripData?.destination || 'trip'}-${new Date()?.toISOString()?.split('T')?.[0]}.pdf`;
      document?.body?.appendChild(link);
      link?.click();
      document?.body?.removeChild(link);
      URL?.revokeObjectURL(url);

      // ✅ UPDATED: Log download activity
      await tripService?.logActivity(
        user?.id,
        tripId,
        'downloaded',
        'Downloaded trip PDF from dashboard',
        {
          downloaded_at: new Date()?.toISOString(),
          location: 'dashboard',
          type: 'pdf_export'
        }
      );

      // ✅ UPDATED: Show success message
      setMessage({ type: 'success', text: 'Download complete!' });
      setTimeout(() => setMessage(null), 3000);

    } catch (err) {
      console.error('PDF download error:', err);
      setMessage({ 
        type: 'error', 
        text: `Download failed: ${err?.message || 'Please try again'}` 
      });
      setTimeout(() => setMessage(null), 5000);
    } finally {
      setDownloadingTripId(null);
    }
  };

  // ✅ NEW: Helper function to convert itinerary to days format for PDF
  const convertItineraryToDaysForPDF = (itineraryData, startDate) => {
    if (!itineraryData || itineraryData?.length === 0) return [];
    
    // Group by day number
    const groupedByDay = itineraryData?.reduce((acc, item) => {
      const dayNumber = item?.dayNumber || 1;
      if (!acc?.[dayNumber]) {
        acc[dayNumber] = [];
      }
      acc?.[dayNumber]?.push(item);
      return acc;
    }, {});

    // Convert to PDF format
    return Object.keys(groupedByDay)?.map((dayNum) => {
      const dayActivities = groupedByDay?.[dayNum];
      const dayDate = new Date(startDate);
      dayDate?.setDate(dayDate?.getDate() + (parseInt(dayNum) - 1));

      return {
        id: `day-${dayNum}`,
        title: dayActivities?.[0]?.title || `Day ${dayNum}`,
        date: dayDate?.toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }),
        activities: dayActivities?.map(activity => ({
          id: activity?.id,
          time: formatTimeForPDF(activity?.startTime) || '9:00 AM',
          title: activity?.title,
          location: activity?.location,
          duration: activity?.endTime ? calculateActivityDurationForPDF(activity?.startTime, activity?.endTime) : '2 hours',
          description: activity?.description,
          notes: activity?.notes || activity?.description,
          activityType: activity?.activityType
        }))
      };
    });
  };

  // ✅ NEW: Format time for PDF
  const formatTimeForPDF = (timeString) => {
    if (!timeString) return '9:00 AM';
    const time = new Date(`2000-01-01T${timeString}`);
    return time?.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  // ✅ NEW: Calculate activity duration for PDF
  const calculateActivityDurationForPDF = (startTime, endTime) => {
    if (!startTime || !endTime) return '2 hours';
    const start = new Date(`2000-01-01T${startTime}`);
    const end = new Date(`2000-01-01T${endTime}`);
    const diffMs = end - start;
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    if (hours === 0) return `${minutes} minutes`;
    if (minutes === 0) return `${hours} hour${hours > 1 ? 's' : ''}`;
    return `${hours} hour${hours > 1 ? 's' : ''} ${minutes} minutes`;
  };

  const handleShareTrip = async (tripId) => {
    try {
      // For now, just log - implement share functionality later
      console.log('Sharing trip:', tripId);
      setMessage({ type: 'info', text: 'Share functionality coming soon!' });
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      console.error('Failed to share trip:', err);
      setMessage({ type: 'error', text: 'Failed to share trip' });
      setTimeout(() => setMessage(null), 3000);
    }
  };

  // ✅ NEW: Handle Browse Examples button click
  const handleBrowseExamples = () => {
    setMessage({ 
      type: 'info', 
      text: '🎉 Coming soon - Ability to browse ParkTrip Planner AI\'s community of curated trips, save/favorite them and add them to your collection!' 
    });
    setTimeout(() => setMessage(null), 5000);
  };

  // Helper function to format itinerary data for PDF
  const formatItineraryForPDF = (itineraryData, startDate) => {
    if (!itineraryData || itineraryData?.length === 0) return [];
    
    // Group by day number
    const groupedByDay = itineraryData?.reduce((acc, item) => {
      const dayNumber = item?.day_number || 1;
      if (!acc?.[dayNumber]) {
        acc[dayNumber] = [];
      }
      acc?.[dayNumber]?.push(item);
      return acc;
    }, {});

    // Convert to PDF format
    return Object.keys(groupedByDay)?.map((dayNum) => {
      const dayActivities = groupedByDay?.[dayNum];
      const dayDate = new Date(startDate);
      dayDate?.setDate(dayDate?.getDate() + (parseInt(dayNum) - 1));

      return {
        id: `day-${dayNum}`,
        title: dayActivities?.[0]?.title || `Day ${dayNum}`,
        date: dayDate?.toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }),
        activities: dayActivities?.map(activity => ({
          id: activity?.id,
          time: formatTime(activity?.start_time) || '9:00 AM',
          title: activity?.title,
          location: activity?.location,
          duration: activity?.end_time ? calculateActivityDuration(activity?.start_time, activity?.end_time) : '2 hours',
          description: activity?.description,
          notes: activity?.notes || activity?.description,
          activityType: activity?.activity_type
        }))
      };
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '9:00 AM';
    const time = new Date(`2000-01-01T${timeString}`);
    return time?.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const calculateActivityDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return '2 hours';
    const start = new Date(`2000-01-01T${startTime}`);
    const end = new Date(`2000-01-01T${endTime}`);
    const diffMs = end - start;
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    if (hours === 0) return `${minutes} minutes`;
    if (minutes === 0) return `${hours} hour${hours > 1 ? 's' : ''}`;
    return `${hours} hour${hours > 1 ? 's' : ''} ${minutes} minutes`;
  };

  const handleSelectAll = () => {
    setSelectedTrips(filteredTrips?.map((trip) => trip?.id));
  };

  const handleDeselectAll = () => {
    setSelectedTrips([]);
  };

  const handleBulkAction = async (action, tripIds) => {
    if (!tripIds?.length) return;

    try {
      switch (action) {
        case 'delete':
          if (!window.confirm(`Are you sure you want to delete ${tripIds?.length} trips?`)) return;
          await Promise.all(tripIds?.map(id => tripService?.deleteTrip(id)));
          // Update local state
          setTrips(prev => prev?.filter(trip => !tripIds?.includes(trip?.id)));
          setSelectedTrips([]);
          // Refresh stats
          const updatedStats = await tripService?.getTripStats(user?.id);
          setStats(updatedStats);
          break;
        case 'archive':
          // For now, just log - implement archive functionality later
          console.log('Archiving trips:', tripIds);
          setSelectedTrips([]);
          break;
        case 'download':
          // For now, just log - implement bulk download later
          console.log('Downloading trips:', tripIds);
          break;
      }
    } catch (err) {
      console.error('Failed to perform bulk action:', err);
      setError('Failed to perform bulk action. Please try again.');
    }
  };

  if (loading || isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header 
          isAuthenticated={!!user}
          user={user}
          onLogout={logout}
        />
        
        {/* Welcome Message for Trip Confirmation Flow */}
        {showWelcomeMessage && (
          <div className="bg-success/10 border border-success/20 mx-4 mt-4 rounded-organic p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-success rounded-organic flex items-center justify-center">
                  <Icon name="CheckCircle" size={20} color="white" />
                </div>
                <div>
                  <h3 className="font-body font-body-medium text-success">
                    Trip Saved Successfully!
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Your itinerary has been saved to your account and you can access it anytime.
                  </p>
                </div>
              </div>
              <button
                onClick={dismissWelcomeMessage}
                className="p-1 hover:bg-muted rounded-organic-sm transition-colors"
              >
                <Icon name="X" size={16} color="var(--color-muted-foreground)" />
              </button>
            </div>
          </div>
        )}

        <main className="pt-20 pb-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="font-body text-muted-foreground">Loading your dashboard...</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header 
          isAuthenticated={!!user}
          user={user}
          onLogout={logout}
        />
        
        {/* Welcome Message for Trip Confirmation Flow */}
        {showWelcomeMessage && (
          <div className="bg-success/10 border border-success/20 mx-4 mt-4 rounded-organic p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-success rounded-organic flex items-center justify-center">
                  <Icon name="CheckCircle" size={20} color="white" />
                </div>
                <div>
                  <h3 className="font-body font-body-medium text-success">
                    Trip Saved Successfully!
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Your itinerary has been saved to your account and you can access it anytime.
                  </p>
                </div>
              </div>
              <button
                onClick={dismissWelcomeMessage}
                className="p-1 hover:bg-muted rounded-organic-sm transition-colors"
              >
                <Icon name="X" size={16} color="var(--color-muted-foreground)" />
              </button>
            </div>
          </div>
        )}

        <main className="pt-20 pb-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center py-12">
              <Icon name="AlertCircle" size={48} className="text-error mx-auto mb-4" />
              <h2 className="font-heading font-heading-bold text-2xl text-foreground mb-2">
                Something went wrong
              </h2>
              <p className="font-body text-muted-foreground mb-6">{error}</p>
              <Button onClick={loadUserTrips}>
                Try Again
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        pageType="dashboard"
        title="My Trips Dashboard - Manage Your Adventures | ParkTrip AI"
        description="View and manage all your saved state park trips. Access your itineraries, upcoming adventures, and past trips in one convenient dashboard."
        canonicalUrl="https://parktripplannerai.com/user-dashboard"
        noindex={true}
      />

      <div className="min-h-screen bg-background">
        <Header 
          isAuthenticated={!!user}
          user={user}
          onLogout={logout}
        />
        {/* Message Display (Success, Error, Info) */}
        {message && (
          <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 max-w-md w-full mx-4">
            <div className={`${
              message?.type === 'success' ? 'bg-success text-success-foreground' :
              message?.type === 'error' ? 'bg-destructive text-destructive-foreground' :
              'bg-primary text-primary-foreground'
            } p-4 rounded-organic shadow-organic-lg border ${
              message?.type === 'success' ? 'border-success/20' :
              message?.type === 'error' ? 'border-destructive/20' : 'border-primary/20'
            }`}>
              <div className="flex items-center space-x-3">
                <Icon 
                  name={
                    message?.type === 'success' ? 'CheckCircle' :
                    message?.type === 'error' ? 'AlertCircle' : 'Download'
                  } 
                  size={24} 
                />
                <div>
                  <p className="text-sm font-medium">{message?.text}</p>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Welcome Message for Trip Confirmation Flow */}
        {showWelcomeMessage && (
          <div className="bg-success/10 border border-success/20 mx-4 mt-4 rounded-organic p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-success rounded-organic flex items-center justify-center">
                  <Icon name="CheckCircle" size={20} color="white" />
                </div>
                <div>
                  <h3 className="font-body font-body-medium text-success">
                    Trip Saved Successfully!
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Your itinerary has been saved to your account and you can access it anytime.
                  </p>
                </div>
              </div>
              <button
                onClick={dismissWelcomeMessage}
                className="p-1 hover:bg-muted rounded-organic-sm transition-colors"
              >
                <Icon name="X" size={16} color="var(--color-muted-foreground)" />
              </button>
            </div>
          </div>
        )}
        <main className="pt-20 pb-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Dashboard Header */}
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="font-heading font-heading-bold text-3xl text-foreground">
                  Welcome back{userProfile?.full_name ? `, ${userProfile?.full_name?.split(' ')?.[0]}` : ''}!
                </h1>
                <p className="font-body text-muted-foreground mt-1">
                  {trips?.length > 0 
                    ? `You have ${trips?.length} saved trip${trips?.length === 1 ? '' : 's'}. Ready for your next adventure?`
                    : "Ready to plan your first state park adventure?"
                  }
                </p>
              </div>
              <button
                onClick={() => navigate('/ai-trip-planning-interface')}
                className="bg-primary text-primary-foreground px-4 py-2 rounded-organic hover:bg-primary/90 transition-organic flex items-center space-x-2"
              >
                <Icon name="Plus" size={16} />
                <span>New Trip</span>
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              {/* Main Content */}
              <div className="lg:col-span-3 space-y-8">
                {/* ✅ NEW: In-Progress Trip Callout (conditionally displayed above AccountSummary) */}
                {isNextTripInProgress && nextTrip && (
                  <div className="bg-gradient-to-r from-orange-50 to-orange-100 border-2 border-orange-300 rounded-organic p-6 shadow-organic-lg">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-orange-500 rounded-organic flex items-center justify-center">
                          <Icon name="Play" size={24} color="white" />
                        </div>
                        <div>
                          <p className="text-sm font-body font-body-medium text-orange-600 uppercase tracking-wide">In-Progress</p>
                          <h3 className="font-heading font-heading-bold text-xl text-foreground">Your Current Adventure</h3>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-body font-body-semibold bg-orange-500 text-white">
                          <Icon name="MapPin" size={14} className="mr-1.5" />
                          Active Now
                        </span>
                      </div>
                    </div>
                    <div className="bg-white rounded-organic p-4 border border-orange-200">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h4 className="font-heading font-heading-bold text-lg text-foreground mb-2">
                            {nextTrip?.title || nextTrip?.destination}
                          </h4>
                          <div className="flex items-center text-sm text-muted-foreground mb-2">
                            <Icon name="MapPin" size={14} className="mr-1" />
                            <span className="font-body-medium">{nextTrip?.park}</span>
                          </div>
                          <div className="flex items-center text-sm text-muted-foreground">
                            <Icon name="Calendar" size={14} className="mr-1" />
                            <span>
                              {new Date(nextTrip?.startDate)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {new Date(nextTrip?.endDate)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <Button
                            onClick={() => navigate('/trip-detail-view', { 
                              state: { 
                                tripId: nextTrip?.id,
                                tripData: nextTrip 
                              } 
                            })}
                            className="bg-orange-500 hover:bg-orange-600 text-white"
                          >
                            View Trip
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Account Summary */}
                <AccountSummary 
                  stats={stats}
                  userProfile={userProfile}
                />

                {/* Search and Filters */}
                <SearchFilters
                  onSearch={handleSearch}
                  onFilter={handleFilter}
                  onClearFilters={handleClearFilters}
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  statusFilter={statusFilter}
                  setStatusFilter={setStatusFilter}
                  sortBy={sortBy}
                  setSortBy={setSortBy}
                  selectedTrips={selectedTrips}
                  setSelectedTrips={setSelectedTrips}
                  trips={trips}
                />

                {/* Trips Grid */}
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h2 className="font-heading font-heading-bold text-xl text-foreground">Your Trips</h2>
                    {selectedTrips?.length > 0 && (
                      <BulkActions 
                        selectedTrips={selectedTrips}
                        onSelectAll={handleSelectAll}
                        onDeselectAll={handleDeselectAll}
                        totalTrips={filteredTrips?.length || 0}
                        onBulkAction={handleBulkAction}
                        onBulkDelete={(ids) => handleBulkAction('delete', ids)}
                        onBulkArchive={(ids) => handleBulkAction('archive', ids)}
                        onBulkDownload={(ids) => handleBulkAction('download', ids)}
                      />
                    )}
                  </div>

                  {filteredTrips?.length === 0 ? (
                    // Empty State
                    <div className="text-center py-12">
                      <div className="w-24 h-24 mx-auto mb-6 bg-muted rounded-organic flex items-center justify-center">
                        <Icon name="MapPin" size={32} color="var(--color-muted-foreground)" />
                      </div>
                      <h3 className="font-heading font-heading-medium text-xl text-foreground mb-2">
                        {trips?.length === 0 ? "No trips yet" : "No trips match your filters"}
                      </h3>
                      <p className="font-body text-muted-foreground mb-6 max-w-md mx-auto">
                        {trips?.length === 0 
                          ? "Start planning your first state park adventure with our AI-powered trip planner." :"Try adjusting your search or filter criteria to find your trips."
                        }
                      </p>
                      {trips?.length === 0 && (
                        <button
                          onClick={() => navigate('/ai-trip-planning-interface')}
                          className="bg-primary text-primary-foreground px-6 py-3 rounded-organic hover:bg-primary/90 transition-organic inline-flex items-center space-x-2"
                        >
                          <Icon name="Plus" size={16} />
                          <span>Plan Your First Trip</span>
                        </button>
                      )}
                    </div>
                  ) : (
                    // Trips Grid
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                      {filteredTrips?.map(trip => (
                        <TripCard
                          key={trip?.id}
                          trip={trip}
                          onSelect={(selected) => {
                            if (selected) {
                              setSelectedTrips(prev => [...prev, trip?.id]);
                            } else {
                              setSelectedTrips(prev => prev?.filter(id => id !== trip?.id));
                            }
                          }}
                          isSelected={selectedTrips?.includes(trip?.id)}
                          onEdit={() => navigate('/trip-review-edit', { 
                            state: { 
                              tripId: trip?.id,
                              tripData: trip 
                            } 
                          })}
                          onView={() => navigate('/trip-detail-view', { 
                            state: { 
                              tripId: trip?.id,
                              tripData: trip 
                            } 
                          })}
                          onDelete={() => handleDeleteTrip(trip?.id)}
                          onDownload={() => handleDownloadTrip(trip?.id)}
                          onShare={() => handleShareTrip(trip?.id)}
                          onDuplicate={() => handleDuplicateTrip(trip)}
                          isDownloading={downloadingTripId === trip?.id}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Quick Actions */}
                <div className="bg-card border border-border rounded-organic p-6 shadow-organic">
                  <h3 className="font-heading font-heading-medium text-lg text-card-foreground mb-4">
                    Quick Actions
                  </h3>
                  <div className="space-y-3">
                    <button
                      onClick={() => navigate('/ai-trip-planning-interface')}
                      className="w-full flex items-center space-x-3 p-3 bg-primary/10 hover:bg-primary/20 rounded-organic transition-colors text-left"
                    >
                      <Icon name="Zap" size={16} color="var(--color-primary)" />
                      <span className="font-body font-body-medium text-card-foreground">AI Trip Planner</span>
                    </button>
                    <button
                      onClick={() => navigate('/user-profile-management')}
                      className="w-full flex items-center space-x-3 p-3 bg-muted hover:bg-muted/80 rounded-organic transition-colors text-left"
                    >
                      <Icon name="Settings" size={16} color="var(--color-muted-foreground)" />
                      <span className="font-body font-body-medium text-card-foreground">Account Settings</span>
                    </button>
                    <button
                      onClick={handleBrowseExamples}
                      className="w-full flex items-center space-x-3 p-3 bg-muted hover:bg-muted/80 rounded-organic transition-colors text-left"
                    >
                      <Icon name="Eye" size={16} color="var(--color-muted-foreground)" />
                      <span className="font-body font-body-medium text-card-foreground">Browse Examples</span>
                    </button>
                  </div>
                </div>

                <RecentActivity activities={activities} />
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default UserDashboard;